import type { FieldContent } from '@capture/shared';

interface FieldActionsProps {
  selectedFieldName: string | null;
  fieldCount: number;
  onAdd: (field: FieldContent) => void;
  onDuplicate: (name: string) => void;
  onDelete: (name: string) => void;
}

export function FieldActions({
  selectedFieldName,
  fieldCount,
  onAdd,
  onDuplicate,
  onDelete,
}: FieldActionsProps) {
  const disabled = !selectedFieldName;

  function handleAdd() {
    // R-141 — label intentionally starts empty so the operator MUST set a
    // human-readable label. Previously defaulted to "New Field" which the
    // drawer happily accepted, producing job types where every field read
    // "New Field" in the Verification Station. IdentitySection's
    // commitLabel rejects empty/whitespace + the "New Field" placeholder
    // so legacy fields edited through the drawer also get caught.
    onAdd({
      fieldDefinitionId: crypto.randomUUID(),
      name: `new_field_${fieldCount + 1}`,
      label: '',
      dataType: 'text',
      scope: 'record',
      // F-006 (2026-07-08 walk) — an empty stageMap left every StageStrip
      // dot inactive with no cue where the field would ever be keyed.
      // Default to editable at Key Entry, matching the convention every
      // legacy field gets via `synthesizeStageMap`
      // (apps/api/src/services/form-config/snapshot-builder.ts).
      stageMap: { 'key-entry': [{ kind: 'display', mode: 'editable' }] },
      displayOrder: fieldCount,
    });
  }

  return (
    <div className="field-actions">
      <button type="button" onClick={handleAdd}>
        Add field
      </button>
      <button
        type="button"
        disabled={disabled}
        onClick={() => selectedFieldName && onDuplicate(selectedFieldName)}
      >
        Duplicate
      </button>
      <button
        type="button"
        disabled={disabled}
        onClick={() => selectedFieldName && onDelete(selectedFieldName)}
      >
        Delete
      </button>
    </div>
  );
}
